import { useCallback, useEffect, useRef, useState } from 'react'

import { CONTAINER_MAX_WIDTH } from '@/config/constants'
import useFeatureFlags from '@/hooks/useFeatureFlags'
import usePermissions from '@/hooks/usePermissions'
import useServiceStatus from '@/hooks/useServiceStatus'
import { Box, Flex } from '@chakra-ui/react'
import { useLocation, useSearchParams } from 'react-router'

import AdminViewBanner from './components/AdminViewBanner'
import AppBar from './components/AppBar'
import Footer from './components/Footer'
import NotificationStrip from './components/NotificationStrip'
import ProjectTable from './components/ProjectTable'
import ProjectTabs from './components/ProjectTabs'
import StatRow from './components/StatRow'
import SubscriptionUpgradeCard from './components/SubscriptionUpgradeCard'
import TermsOfServiceModal from './components/TermsOfServiceModal'
import useProjects from './hooks/useProjects'
import useUser from './hooks/useUser'
import { ProjectTab, canCreateProject, getProjectSize, hasDeprecatedSubscription, parseDashboardHash } from './utils'

const ProjectList = () => {
  // Data
  const { user, agreeToTos } = useUser()
  const { ownedProjects, invitedProjects, isLoadingOwned, isLoadingInvited } = useProjects()
  const { serviceStatus } = useServiceStatus()
  const { featureFlags } = useFeatureFlags()
  const { project: projectPermission, isReadOnly, isViewingOtherUser, hasBaseAccess } = usePermissions()
  const [searchParams] = useSearchParams()
  const location = useLocation()

  // States
  const [activeTab, setActiveTab] = useState(ProjectTab.Owned)

  // If the dashboard was opened with a project id in the URL hash (the editor's back-to-
  // dashboard button) and that project is only in the invited list, switch to the invited tab
  // once the lists resolve — otherwise its row (and its auto-expanded area sub-table) would sit
  // on the hidden tab. Owned projects stay on the default tab. Runs a single time.
  const didSelectHashTab = useRef(false)
  useEffect(() => {
    if (didSelectHashTab.current) return
    const { projectId: hashProjectId } = parseDashboardHash(location.hash)
    if (!hashProjectId) return
    const inOwned = ownedProjects.some((p) => p.project_id === hashProjectId)
    const inInvited = invitedProjects.some((p) => p.project_id === hashProjectId)
    if (!inOwned && !inInvited) return
    didSelectHashTab.current = true
    if (!inOwned && inInvited) setActiveTab(ProjectTab.Invited)
  }, [location.hash, ownedProjects, invitedProjects])

  // Derived state
  const notification = serviceStatus?.notification?.enabled ? serviceStatus.notification : undefined
  const isOwnedTab = activeTab === ProjectTab.Owned
  const activeProjects = isOwnedTab && hasBaseAccess ? ownedProjects : invitedProjects
  const isLoadingActive = isOwnedTab && hasBaseAccess ? isLoadingOwned : isLoadingInvited

  // Subscription capability drives the upgrade card; the effective create permission (which
  // also respects the admin read-only view) drives the create button.
  const canSubscribe = canCreateProject(user)

  // When an admin opens another user's dashboard from the user list, that user's storage and
  // email ride along in navigation state (both already on the fetched profile).
  const navState = location.state as { storageUsed?: number; userEmail?: string } | null

  // Storage gauge: the viewed user's total (falling back to the sum of their owned projects on
  // a fresh load where the carried value is gone), or the signed-in user's own total.
  const storageUsed = isViewingOtherUser
    ? (navState?.storageUsed ?? ownedProjects.reduce((total, project) => total + getProjectSize(project), 0))
    : (user?.stats?.total_size ?? 0)
  const viewedUserLabel = navState?.userEmail ?? searchParams.get('user_id') ?? ''

  // Agreement gate — like the legacy dashboard, users on self-managed subscriptions must
  // agree to the current ToS version before using the app (after sign-up or a ToS update).
  const tosFlag = featureFlags?.selfManagedSubscriptions
  const needsTosAgreement = Boolean(
    user && tosFlag?.enabled && !hasDeprecatedSubscription(user) && user.agreed_tos_version !== tosFlag.tos_version,
  )

  /**
   * Record the user's agreement to the current ToS version; the cached profile updates,
   * which unmounts the gate.
   */
  const handleAgreeToTos = useCallback(async () => {
    if (!tosFlag) return
    await agreeToTos({ tosVersion: tosFlag.tos_version })
  }, [agreeToTos, tosFlag])

  return (
    <Flex direction="column" minH="100vh" bg="#F6F8FB">
      {notification && <NotificationStrip notification={notification} />}
      <AppBar />
      <Box as="main" flex="1" w="100%" maxW={CONTAINER_MAX_WIDTH} mx="auto" px="28px" pt="34px" pb="64px">
        {isViewingOtherUser && <AdminViewBanner userLabel={viewedUserLabel} />}
        <StatRow storageUsed={storageUsed} ownedCount={ownedProjects.length} invitedCount={invitedProjects.length} />
        {/* Shown only once the profile has loaded, so the card doesn't flash for paying users.
            Hidden for admins, who view in read-only mode and don't subscribe here. */}
        {Boolean(user) && !canSubscribe && !isReadOnly && <SubscriptionUpgradeCard />}
        <ProjectTabs
          activeTab={activeTab}
          ownedCount={ownedProjects.length}
          invitedCount={invitedProjects.length}
          hasBaseAccess={hasBaseAccess}
          onSelect={setActiveTab}
        />
        {/* Remount per tab so each list starts with a clean search/sort. */}
        <ProjectTable
          key={activeTab}
          projects={activeProjects}
          isLoading={isLoadingActive}
          isOwned={isOwnedTab}
          canCreate={projectPermission.create}
        />
      </Box>
      <Footer />

      {needsTosAgreement && <TermsOfServiceModal url={tosFlag!.tos_url} onAgree={handleAgreeToTos} />}
    </Flex>
  )
}

export default ProjectList
